import { useEffect, useState } from 'react';
import { Pressable, TextInput, View } from 'react-native';

import { Text } from '@/components/ui';

import { fetchCurrentMember, updateBlock } from '@/lib/blocks/api';
import { displayBlockTitle, isWorkspaceRootBlock } from '@/lib/blocks/constants';
import { copyPageAsMarkdown, downloadPageAsMarkdown } from '@/lib/blocks/page-export';
import type { BlockRecord } from '@/lib/blocks/types';

import { FavoriteStarButton } from './FavoriteStarButton.web';
import { PrivateFlagButton } from './PrivateFlagButton.web';
import { PageMenu } from './PageMenu';
import { useSidebar } from './SidebarContext';

type PageHeaderProps = {
  block: BlockRecord;
  displayTitle?: string;
  onTitleChange?: (title: string) => void;
};

function selectInputTextOnFocus(event: { nativeEvent: { target?: unknown } }) {
  const target = event.nativeEvent.target as HTMLInputElement | null;
  target?.select();
}

export function PageHeader({ block, displayTitle, onTitleChange }: PageHeaderProps) {
  const { updateChild } = useSidebar();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(block.title ?? 'Untitled');
  const [saving, setSaving] = useState(false);
  const [titleLocked, setTitleLocked] = useState(false);
  const [memberId, setMemberId] = useState<string | null>(null);

  useEffect(() => {
    setTitle(displayBlockTitle(block, memberId));
  }, [block.id, block.title, block.createdBy, block.content, memberId]);

  useEffect(() => {
    if (displayTitle !== undefined) {
      setTitle(displayTitle);
    }
  }, [displayTitle]);

  useEffect(() => {
    let cancelled = false;
    void fetchCurrentMember()
      .then((member) => {
        if (!cancelled) {
          setMemberId(member.id);
          setTitle(displayBlockTitle(block, member.id));
          setTitleLocked(isWorkspaceRootBlock(block, member.id));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTitleLocked(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [block]);

  async function saveTitle(nextTitle: string): Promise<void> {
    if (titleLocked) {
      return;
    }
    const trimmed = nextTitle.trim() || 'Untitled';
    setTitle(trimmed);
    setSaving(true);
    try {
      const updated = await updateBlock(block.id, {
        title: trimmed,
        expectedSyncVersion: block.syncVersion,
      });
      onTitleChange?.(trimmed);
      if (updated.parentId) {
        updateChild(updated.parentId, updated.id, {
          title: updated.title,
          syncVersion: updated.syncVersion,
        });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <View
      className="mx-auto w-full max-w-content shrink-0 flex-row items-start justify-between gap-3 px-page pb-2 pt-6"
      style={{ zIndex: 2 }}
    >
      <View className="min-w-0 flex-1 items-start">
        {editing && !titleLocked ? (
          <TextInput
            autoFocus
            className="cs-text-display w-full shrink-0 self-start text-left text-fg"
            onBlur={() => {
              setEditing(false);
              void saveTitle(title);
            }}
            onChangeText={setTitle}
            onFocus={selectInputTextOnFocus}
            onSubmitEditing={() => {
              setEditing(false);
              void saveTitle(title);
            }}
            selectTextOnFocus
            value={title}
          />
        ) : (
          <Pressable
            className="shrink-0 self-start text-left"
            disabled={titleLocked}
            onPress={() => setEditing(true)}
          >
            <Text variant="display" className="text-left">
              {title}
            </Text>
          </Pressable>
        )}
        {block.updatedBy ? (
          <Text variant="caption" className="mt-1 shrink-0 text-left">
            {saving ? 'Saving…' : 'Last edited recently'}
          </Text>
        ) : null}
      </View>
      {!titleLocked && (block.type === 'page' || block.type === 'database') ? (
        <View className="flex shrink-0 flex-row items-center gap-1">
          {block.type === 'page' ? (
            <PageMenu
              disabled={saving}
              onCopyMarkdown={() => copyPageAsMarkdown(block.id)}
              onExportMarkdown={() => downloadPageAsMarkdown(block.id)}
              pageId={block.id}
            />
          ) : null}
          <PrivateFlagButton blockId={block.id} />
          <FavoriteStarButton blockId={block.id} />
        </View>
      ) : null}
    </View>
  );
}
